import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Input } from "@/shared";
import { useAuth, useConfig } from "@/hooks";
import helpCoverageRegistry from "./coverage/coverage.registry";
import {
  isFeatureEnabled,
  isFeatureAvailableForAppType,
  buildDocsLibraryUrl,
  buildDocsSearchUrl,
} from "./docs.contract";
import { getFeatureHelpPath, getDocsUrl, matchesHelpQuery } from "./help-links";

const OPEN_HELP_HUB_EVENT = "attune:help-hub-open";
const OPEN_TUTORIAL_EVENT = "attune:tutorial-open";

export function HelpHubModal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { configs, appType } = useConfig();
  const featureFlags = configs?.featureFlags || {};

  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handleOpen = () => setVisible(true);
    window.addEventListener(OPEN_HELP_HUB_EVENT, handleOpen);
    return () => {
      window.removeEventListener(OPEN_HELP_HUB_EVENT, handleOpen);
    };
  }, []);

  const featureCards = useMemo(() => {
    return helpCoverageRegistry
      .filter((item) => isFeatureEnabled(featureFlags, item.featureKey))
      .filter((item) => isFeatureAvailableForAppType(item.appTypes, appType))
      .filter((item) => matchesHelpQuery(item, query));
  }, [featureFlags, appType, query]);

  const openHelpSection = (item) => {
    setVisible(false);
    navigate(getFeatureHelpPath(item));
  };

  const docsLibraryUrl = buildDocsLibraryUrl();
  const docsSearchUrl = buildDocsSearchUrl(query);

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      onClose={() => setVisible(false)}
      size="xl"
      title="Help & Onboarding Hub"
      hideFooter
      contentWrapperClass="p-4 md:p-6 space-y-4"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Search in-app guides, look up trucking words, or open the docs
          library — a searchable site for the full dictionary and customer docs.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            type="primary"
            size="sm"
            onClick={() => {
              setVisible(false);
              window.dispatchEvent(
                new CustomEvent(OPEN_TUTORIAL_EVENT, {
                  detail: { type: "full" },
                })
              );
            }}
          >
            Start onboarding walkthrough
          </Button>
          <Button
            type="alternative"
            size="sm"
            onClick={() => {
              setVisible(false);
              navigate("/help/features");
            }}
          >
            Open Feature Docs
          </Button>
          <a href={docsLibraryUrl} target="_blank" rel="noreferrer">
            <Button type="alternative" size="sm">
              Open docs library
            </Button>
          </a>
          <a href={docsSearchUrl} target="_blank" rel="noreferrer">
            <Button type="alternative" size="sm">
              {query.trim()
                ? `Search docs for “${query.trim()}”`
                : "Search all docs"}
            </Button>
          </a>
        </div>

        <Input
          id="help-hub-search"
          value={query}
          onChange={({ value }) => setQuery(value)}
          placeholder="Search guides, BOL, POD, FTL, HOS…"
          label="Find a guide"
          size="sm"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
          {featureCards.map((item) => {
            const docsUrl = getDocsUrl(item, user);
            return (
            <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-3">
              <h4 className="text-sm font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-xs text-gray-600">{item.description}</p>
              <div className="mt-2 text-[11px] text-gray-500">
                Status: {item.guideStatus} · Screenshots: {item.screenshotStatus}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  type="primary"
                  size="sm"
                  onClick={() => openHelpSection(item)}
                >
                  {item.helpKind === "page" ? "Open dictionary" : "Open help section"}
                </Button>
                {item.helpKind !== "page" && (
                  <Button
                    type="alternative"
                    size="sm"
                    onClick={() => {
                      setVisible(false);
                      navigate(item.inAppPath);
                    }}
                  >
                    Open in app
                  </Button>
                )}
                {docsUrl && (
                  <a
                    href={docsUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button type="alternative" size="sm">
                      Open docs
                    </Button>
                  </a>
                )}
              </div>
            </div>
            );
          })}
          {featureCards.length === 0 && (
            <div className="col-span-1 md:col-span-2 rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              No guides match your search.{" "}
              <a
                href={docsSearchUrl}
                target="_blank"
                rel="noreferrer"
                className="text-primary-600 underline"
              >
                Search the docs library
              </a>
              .
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default HelpHubModal;
